import React, { useEffect, useRef, useState } from 'react';
import { Button, Table } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import type { PriceResponseDto } from '../../api-schema/generated/types';
import type { BoardSymbol } from '../../services/priceBoardApi';
import { formatInt, formatPercent } from '../../utils/numberFormat';
import { boardPrice, FlashField, flashesBetween, pctOf, TONE_COLOR, toneOf } from './boardMath';

interface Props {
    rows: BoardSymbol[];
    prices: Record<string, PriceResponseDto>;
    selected: string | null;
    onSelect: (symbol: string) => void;
    compact?: boolean;
    loading?: boolean;
    /** Set only for the watchlist: adds a remove button to each row. */
    onRemove?: (symbol: string) => void;
}

const FLASH_MS = 900;
/** `symbol:field` -> direction and a token that restarts the animation on the next change. */
type Flashes = Record<string, { dir: 'up' | 'down'; token: number }>;

export const FLASH_CSS = `
@keyframes pbFlashUp { from { background: rgba(14, 203, 129, 0.45); } to { background: transparent; } }
@keyframes pbFlashDown { from { background: rgba(246, 70, 93, 0.45); } to { background: transparent; } }
.pb-cell { display: block; margin: -4px -8px; padding: 4px 8px; border-radius: 2px; }
.pb-flash-up { animation: pbFlashUp ${FLASH_MS}ms ease-out; }
.pb-flash-down { animation: pbFlashDown ${FLASH_MS}ms ease-out; }
`;

/** Flash a cell whose value a tick just moved: green up, red down. */
function useFlashes(prices: Record<string, PriceResponseDto>): Flashes {
    const prev = useRef(prices);
    const [flashes, setFlashes] = useState<Flashes>({});
    const counter = useRef(0);
    useEffect(() => {
        const changes = flashesBetween(prev.current as any, prices as any);
        prev.current = prices;
        if (!changes.length) return;
        setFlashes((f) => {
            const next = { ...f };
            for (const c of changes) next[`${c.symbol}:${c.field}`] = { dir: c.dir, token: ++counter.current };
            return next;
        });
    }, [prices]);
    // Drop finished flashes so a later identical change still animates.
    useEffect(() => {
        if (!Object.keys(flashes).length) return;
        const id = window.setTimeout(() => setFlashes({}), FLASH_MS + 100);
        return () => window.clearTimeout(id);
    }, [flashes]);
    return flashes;
}

/** Column set, exported so the fixed-width rule can be checked on the definition itself. */
export function boardColumns(
    prices: Record<string, PriceResponseDto>,
    compact: boolean,
    flashes: Flashes = {},
    onRemove?: (symbol: string) => void,
): ColumnsType<BoardSymbol> {
    const q = (s: string) => prices[s];
    const toneColor = (s: string, v: number | null | undefined) =>
        TONE_COLOR[toneOf(v, q(s)?.ref_price, q(s)?.ceiling_price, q(s)?.floor_price)];
    const flashed = (s: string, field: FlashField, content: React.ReactNode, color?: string, bold?: boolean) => {
        const f = flashes[`${s}:${field}`];
        return (
            <span key={f?.token ?? 0} className={`pb-cell${f ? ` pb-flash-${f.dir}` : ''}`} style={{ color, fontWeight: bold ? 700 : undefined }}>
                {content}
            </span>
        );
    };
    const priceCell = (field: FlashField, bold = false) => (_: unknown, r: BoardSymbol) => {
        const v = (q(r.symbol) as any)?.[field] as number | undefined;
        return flashed(r.symbol, field, boardPrice(r.symbol, v), toneColor(r.symbol, v), bold);
    };
    const qtyCell = (field: FlashField) => (_: unknown, r: BoardSymbol) =>
        flashed(r.symbol, field, formatInt((q(r.symbol) as any)?.[field]));
    const band = (label: string, key: 'ceiling_price' | 'floor_price' | 'ref_price', color: string) => ({
        title: label,
        key,
        width: 84,
        align: 'right' as const,
        render: (_: unknown, r: BoardSymbol) => <span style={{ color }}>{boardPrice(r.symbol, q(r.symbol)?.[key])}</span>,
    });
    // 24px with no cell padding, so the phone layout still fits 360px.
    const remove: ColumnsType<BoardSymbol> = onRemove
        ? [
              {
                  title: '',
                  key: 'remove',
                  width: 24,
                  align: 'center',
                  onCell: () => ({ style: { padding: 0 } }),
                  render: (_: unknown, r: BoardSymbol) => (
                      <Button
                          type="text"
                          size="small"
                          icon={<CloseOutlined />}
                          aria-label="Remove from watchlist"
                          title="Remove from watchlist"
                          style={{ color: '#848E9C' }}
                          onClick={(e) => {
                              e.stopPropagation(); // not a row click
                              onRemove(r.symbol);
                          }}
                      />
                  ),
              },
          ]
        : [];

    // Phone: three columns that fit 360px — the symbol (tenor under it), last with its change
    // under it, and volume. The full book is one tap away in the symbol panel.
    if (compact) {
        return [
            {
                title: 'Symbol',
                dataIndex: 'symbol',
                width: 96,
                render: (s: string, r) => (
                    <div style={{ lineHeight: 1.25 }}>
                        <div style={{ fontWeight: 600, color: toneColor(s, q(s)?.latest_matched_price) }}>{s}</div>
                        <div style={{ color: '#848E9C', fontSize: 11 }}>{r.tenor ?? r.exchange}</div>
                    </div>
                ),
            },
            {
                title: 'Last · %',
                key: 'last',
                width: 108,
                align: 'right',
                render: (_: unknown, r) => {
                    const d = q(r.symbol);
                    const color = toneColor(r.symbol, d?.latest_matched_price);
                    return (
                        <div style={{ lineHeight: 1.25 }}>
                            {flashed(r.symbol, 'latest_matched_price', boardPrice(r.symbol, d?.latest_matched_price), color, true)}
                            <div style={{ color, fontSize: 11 }}>{formatPercent(pctOf(d?.latest_matched_price, d?.ref_price), 2, { signed: true })}</div>
                        </div>
                    );
                },
            },
            { title: 'Volume', key: 'vol', width: 96, align: 'right', render: qtyCell('total_matched_quantity') },
            ...remove,
        ];
    }

    return [
        {
            title: 'Symbol',
            dataIndex: 'symbol',
            fixed: 'left',
            width: 136,
            render: (s: string, r) => (
                <span style={{ fontWeight: 600, color: toneColor(s, q(s)?.latest_matched_price) }}>
                    {s}
                    {r.tenor && <span style={{ color: '#848E9C', fontWeight: 400, marginLeft: 4, fontSize: 11 }}>{r.tenor}</span>}
                </span>
            ),
        },
        ...[
                  band('Ceil', 'ceiling_price', TONE_COLOR.ceiling),
                  band('Floor', 'floor_price', TONE_COLOR.floor),
                  band('Ref', 'ref_price', TONE_COLOR.ref),
                  { title: 'Bid 1', key: 'bid1', width: 84, align: 'right' as const, render: priceCell('bid_price_1') },
                  { title: 'Bid vol', key: 'bidv1', width: 88, align: 'right' as const, render: qtyCell('bid_quantity_1') },
              ],
        { title: 'Last', key: 'last', width: 90, align: 'right', render: priceCell('latest_matched_price', true) },
        {
            title: '%',
            key: 'pct',
            width: 76,
            align: 'right',
            render: (_: unknown, r) => {
                const d = q(r.symbol);
                return (
                    <span style={{ color: toneColor(r.symbol, d?.latest_matched_price) }}>
                        {formatPercent(pctOf(d?.latest_matched_price, d?.ref_price), 2, { signed: true })}
                    </span>
                );
            },
        },
        ...[
                  { title: 'Ask 1', key: 'ask1', width: 84, align: 'right' as const, render: priceCell('ask_price_1') },
                  { title: 'Ask vol', key: 'askv1', width: 88, align: 'right' as const, render: qtyCell('ask_quantity_1') },
                  { title: 'High', key: 'high', width: 84, align: 'right' as const, render: priceCell('highest_price') },
                  { title: 'Low', key: 'low', width: 84, align: 'right' as const, render: priceCell('lowest_price') },
              ],
        { title: 'Volume', key: 'vol', width: 104, align: 'right', render: qtyCell('total_matched_quantity') },
        ...remove,
    ];
}

/** One row per symbol, coloured the way every Vietnamese board is: against ref, band first. */
const BoardTable: React.FC<Props> = ({ rows, prices, selected, onSelect, compact = false, loading, onRemove }) => {
    const flashes = useFlashes(prices);
    return (
        <>
            <style>{FLASH_CSS}</style>
            <Table<BoardSymbol>
                size="small"
                rowKey="symbol"
                loading={loading}
                dataSource={rows}
                columns={boardColumns(prices, compact, flashes, onRemove)}
                pagination={false}
                // Fixed widths: an auto layout re-measures on every tick, so a quantity going from
                // one digit to two shifted every column.
                tableLayout="fixed"
                className="price-board-table"
                scroll={{ x: 'max-content' }}
                onRow={(r) => ({
                    onClick: () => onSelect(r.symbol),
                    style: { cursor: 'pointer', ...(r.symbol === selected ? { background: 'rgba(14,203,129,0.08)', '--row-tint': 'rgba(14,203,129,0.08)' } : {}) } as React.CSSProperties,
                })}
                locale={{ emptyText: 'No symbols' }}
            />
        </>
    );
};

export default BoardTable;
